/**
 * MemberPortalLayout — shared shell for all authenticated member-portal routes
 * (react-router layout route with <Outlet/>).
 *
 * Brings the member portal to parity with the staff web app: a left V2-style
 * navigation rail (MemberSidebar) + a scrollable content area, replacing the
 * old top-bar. Pages render their own <PageHeader> (title/back/actions) +
 * <PageContainer> inside the content area.
 *
 * It also owns the member's theme: on mount it applies the locally-stored theme
 * immediately (instant paint, shared with the index.html boot script), then
 * loads the member's saved theme from GET /api/member-portal/settings and
 * applies it so the choice follows the member across devices. All member pages
 * already use `dark:` Tailwind classes, so toggling the `dark` class on <html>
 * is all that's needed.
 */
import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { MemberSidebar } from '@/components/member-portal/MemberSidebar';
import {
  applyTheme,
  getStoredTheme,
  loadMemberPreferences,
  persistThemeLocal,
} from '@/lib/memberPreferences';
import { getMemberToken } from '@/lib/memberAuth';
import { useWebPushNavigation, useWebPushSync } from '@/hooks/useWebPush';
import { parsePushTarget, routeMemberPushTarget } from '@/lib/pushNavigation';
import { WebPushBanner } from '@/components/messaging/WebPushBanner';
import { MemberMessageNotifier } from '@/components/member-portal/MemberMessageNotifier';
import { ImpersonationBanner } from '@/components/impersonation/ImpersonationBanner';
import { isImpersonating } from '@/lib/impersonation';

export default function MemberPortalLayout() {
  // Browser push (TRUS-668): silent re-registration when already allowed.
  // Never while a super admin is viewing as this member (TRUS-747): their
  // browser must not receive the member's notifications.
  const viewingAs = isImpersonating();
  useWebPushSync(!!getMemberToken() && !viewingAs, 'member');
  // A clicked notification routes in-app (no reload, which would drop a call).
  const navigate = useNavigate();
  useWebPushNavigation(!!getMemberToken() && !viewingAs, (url) => {
    const target = parsePushTarget(url, window.location.origin);
    return target ? routeMemberPushTarget(target, navigate) : false;
  });
  useEffect(() => {
    // 1) Instant: apply whatever the member last chose on this device.
    const stored = getStoredTheme();
    if (stored) applyTheme(stored);

    // 2) Cross-device: reconcile with the server-saved theme.
    let cancelled = false;
    loadMemberPreferences().then((prefs) => {
      if (cancelled || !prefs) return;
      applyTheme(prefs.theme);
      persistThemeLocal(prefs.theme);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="flex h-screen overflow-hidden bg-paper text-ink">
      <ImpersonationBanner />
      <MemberSidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-y-auto">
        {/* "Turn on notifications" (TRUS-668): every page load / sign-in until this browser is registered. */}
        {getMemberToken() && !viewingAs && <WebPushBanner portal="member" />}
        {/* Desktop notifications for new messages, with or without web push (TRUS-668). */}
        {getMemberToken() && <MemberMessageNotifier />}
        <Outlet />
      </div>
    </div>
  );
}
